import type { JSONContent } from '@tiptap/core';

import { getExtensions } from '@/components/tiptap-editor/tiptap-editor';
import type { SelectedZyVersionFilter } from '../zy-tiptap-utils';
import { renderJsonTo } from '../zy-tiptap-utils';
import type {
    ConvertZybookTestQuestionsOptions,
    ZybookTestQuestion,
    ZybookTestQuestionNode,
    ZybookTestQuestionTextNodeMark,
} from './from-test-question';
import { buildColorizedZyCodeHtml, transformDoc } from './to-zybooks-json';

export interface ConvertProsemirrorTestQuestionsDeps {
    getExtensions: typeof getExtensions;
    transformDoc: typeof transformDoc;
    buildColorizedZyCodeHtml: typeof buildColorizedZyCodeHtml;
}

const browserTestQuestionConversionDeps: ConvertProsemirrorTestQuestionsDeps = {
    getExtensions,
    transformDoc,
    buildColorizedZyCodeHtml,
};

export type ConvertProsemirrorToZybookTestQuestionsOptions = ConvertZybookTestQuestionsOptions & {
    dev_book?: string | null;
    gdoc_parent_folder?: string | null;
    canonical_section_id?: number;
    subject_id?: number;
    release_id?: number;
    selectedZyVersion?: SelectedZyVersionFilter;
    number?: number;
};

function reverseImageSrc(src: string | undefined): string {
    if (!src) {
        return '';
    }
    const zyAuthorIndex = src.indexOf('zyAuthor/');
    if (zyAuthorIndex >= 0) {
        return src.slice(zyAuthorIndex).split('?')[0];
    }
    return src.split('?')[0];
}

function convertProsemirrorMarks(marks?: JSONContent['marks']): { type?: ZybookTestQuestionTextNodeMark }[] | undefined {
    if (!marks?.length) {
        return undefined;
    }

    const converted: { type?: ZybookTestQuestionTextNodeMark }[] = [];
    for (const mark of marks) {
        if (mark.type === 'code') {
            converted.push({ type: 'code' });
        } else if (mark.type === 'italic') {
            converted.push({ type: 'italic' });
        } else if (mark.type === 'bold') {
            converted.push({ type: 'bold' });
        }
    }

    return converted.length > 0 ? converted : undefined;
}

function convertProsemirrorInlineNodes(
    nodes: JSONContent[] | undefined,
    deps: ConvertProsemirrorTestQuestionsDeps,
): ZybookTestQuestionNode[] {
    if (!nodes?.length) {
        return [];
    }

    const converted: ZybookTestQuestionNode[] = [];
    for (const node of nodes) {
        if (node.type === 'inlineMath') {
            const latex = typeof node.attrs?.latex === 'string' ? node.attrs.latex : '';
            converted.push({
                kind: 'text',
                text: `\\(${latex}\\)`,
                marks: [{ type: 'ilatex' }],
            });
            continue;
        }

        if (node.type === 'hardBreak') {
            converted.push({ kind: 'text', text: '\n' });
            continue;
        }

        if (node.type === 'text') {
            converted.push({
                kind: 'text',
                text: renderJsonTo(node, deps.getExtensions(), 'html') ?? '',
                marks: convertProsemirrorMarks(node.marks),
            });
        }
    }

    return converted;
}

async function convertZyCodeToCodeBlock(node: JSONContent, deps: ConvertProsemirrorTestQuestionsDeps): Promise<ZybookTestQuestionNode> {
    const attrs = node.attrs || {};
    const rawText = (node.content || []).map(child => child.text || '').join('');
    const htmlOutput = await deps.buildColorizedZyCodeHtml(rawText, attrs);

    return {
        kind: 'block',
        type: 'codeBlock',
        data: {
            language: typeof attrs.language === 'string' ? attrs.language : 'python',
            raw_text: rawText,
        },
        nodes: [{ kind: 'text', text: htmlOutput }],
    };
}

function convertTableCellContent(nodes: JSONContent[] | undefined, deps: ConvertProsemirrorTestQuestionsDeps): ZybookTestQuestionNode[] {
    if (!nodes?.length) {
        return [{ kind: 'text', text: '' }];
    }

    const converted: ZybookTestQuestionNode[] = [];
    for (const node of nodes) {
        if (node.type === 'paragraph' || node.type === 'heading') {
            converted.push(...convertProsemirrorInlineNodes(node.content, deps));
        }
    }

    return converted.length > 0 ? converted : [{ kind: 'text', text: '' }];
}

function convertTableHeaderToZybookHeader(node: JSONContent, deps: ConvertProsemirrorTestQuestionsDeps): ZybookTestQuestionNode {
    const data: Record<string, unknown> = {};
    if (node.attrs?.primaryKey) {
        data.class = 'dbprimarykey';
    } else if (node.attrs?.foreignKey) {
        data.class = 'dbforeignkey';
    }

    return {
        kind: 'block',
        type: 'header',
        data,
        nodes: convertTableCellContent(node.content, deps),
    };
}

function convertTableCellToZybookCell(node: JSONContent, deps: ConvertProsemirrorTestQuestionsDeps): ZybookTestQuestionNode {
    return {
        kind: 'block',
        type: 'cell',
        data: {},
        nodes: convertTableCellContent(node.content, deps),
    };
}

function convertTableRowToZybookRow(node: JSONContent, deps: ConvertProsemirrorTestQuestionsDeps): ZybookTestQuestionNode {
    const cells: ZybookTestQuestionNode[] = [];
    for (const child of node.content || []) {
        if (child.type === 'tableHeader') {
            cells.push(convertTableHeaderToZybookHeader(child, deps));
        } else if (child.type === 'tableCell') {
            cells.push(convertTableCellToZybookCell(child, deps));
        }
    }

    return {
        kind: 'block',
        type: 'row',
        data: {},
        nodes: cells,
    };
}

function convertTableToZybookTable(node: JSONContent, deps: ConvertProsemirrorTestQuestionsDeps): ZybookTestQuestionNode {
    const data: Record<string, unknown> = {};
    if (node.attrs?.isDatabaseTable) {
        data.class = 'dbtable';
    }

    return {
        kind: 'block',
        type: 'table',
        data,
        nodes: (node.content || []).filter(child => child.type === 'tableRow').map(child => convertTableRowToZybookRow(child, deps)),
    };
}

function convertImageToZybookImage(node: JSONContent): ZybookTestQuestionNode {
    const attrs = node.attrs || {};
    const url = reverseImageSrc(typeof attrs.src === 'string' ? attrs.src : undefined);
    const fileName = url
        .split('/')
        .pop()
        ?.replace(/\.[^.]+$/, '');

    const data: Record<string, unknown> = {
        alt: typeof attrs.alt === 'string' ? attrs.alt : '',
        url,
    };

    if (attrs.width != null) {
        data.width = String(attrs.width);
    }
    if (fileName) {
        data.guid = fileName;
    }

    return {
        kind: 'block',
        type: 'image',
        data,
        nodes: null,
        isVoid: true,
    };
}

function convertBlockMathToBlatexText(node: JSONContent): ZybookTestQuestionNode {
    const latex = typeof node.attrs?.latex === 'string' ? node.attrs.latex : '';
    return {
        kind: 'text',
        text: `\\[${latex}\\]`,
        marks: [{ type: 'blatex' }],
    };
}

function paragraphContentHasHardBreak(content: JSONContent[] | undefined): boolean {
    return content?.some(node => node.type === 'hardBreak') ?? false;
}

function splitParagraphContentByHardBreak(content: JSONContent[] | undefined): JSONContent[][] {
    if (!content?.length) {
        return [[]];
    }

    const segments: JSONContent[][] = [[]];
    for (const node of content) {
        if (node.type === 'hardBreak') {
            segments.push([]);
            continue;
        }
        segments[segments.length - 1]!.push(node);
    }

    return segments;
}

function convertParagraphToZybookBlocks(node: JSONContent, deps: ConvertProsemirrorTestQuestionsDeps): ZybookTestQuestionNode[] {
    const content = node.content;
    if (!paragraphContentHasHardBreak(content)) {
        return [
            {
                kind: 'block',
                type: 'paragraph',
                nodes: convertProsemirrorInlineNodes(content, deps),
            },
        ];
    }

    return splitParagraphContentByHardBreak(content).map(segment => ({
        kind: 'block',
        type: 'line',
        data: {},
        nodes: convertProsemirrorInlineNodes(segment, deps),
    }));
}

async function convertProsemirrorBlockNodes(
    nodes: JSONContent[] | undefined,
    deps: ConvertProsemirrorTestQuestionsDeps,
): Promise<ZybookTestQuestionNode[]> {
    if (!nodes?.length) {
        return [
            {
                kind: 'block',
                type: 'paragraph',
                data: {},
                nodes: [{ kind: 'text', text: '' }],
            },
        ];
    }

    const converted: ZybookTestQuestionNode[] = [];
    for (const node of nodes) {
        switch (node.type) {
            case 'paragraph':
                converted.push(...convertParagraphToZybookBlocks(node, deps));
                break;
            case 'zyCode':
                converted.push(await convertZyCodeToCodeBlock(node, deps));
                break;
            case 'image':
                converted.push(convertImageToZybookImage(node));
                break;
            case 'table':
                converted.push(convertTableToZybookTable(node, deps));
                break;
            case 'blockMath':
                converted.push(convertBlockMathToBlatexText(node));
                break;
            default:
                converted.push({
                    kind: 'text',
                    text: renderJsonTo(node, deps.getExtensions(), 'html') ?? '',
                });
                break;
        }
    }

    return converted.length > 0
        ? converted
        : [
              {
                  kind: 'block',
                  type: 'paragraph',
                  data: {},
                  nodes: [{ kind: 'text', text: '' }],
              },
          ];
}

function flattenInlineParagraphBlocks(nodes: ZybookTestQuestionNode[]): ZybookTestQuestionNode[] {
    if (nodes.length === 1 && nodes[0].kind === 'block' && nodes[0].type === 'paragraph') {
        return nodes[0].nodes ?? [];
    }
    return nodes;
}

function applyQuestionMetadata(
    question: ZybookTestQuestion,
    attrs: Record<string, unknown>,
    options?: ConvertProsemirrorToZybookTestQuestionsOptions,
): ZybookTestQuestion {
    if (typeof options?.number === 'number') {
        question.number = options.number;
    }
    if (typeof attrs.testQuestionId === 'number') {
        question.test_question_id = attrs.testQuestionId;
    }
    if (typeof attrs.id === 'string') {
        question.guid = attrs.id;
    }
    if (typeof attrs.gdocId === 'string') {
        question.gdoc_id = attrs.gdocId;
    }
    if (typeof attrs.showInEval === 'boolean') {
        question.show_in_eval = attrs.showInEval;
    }

    if (options?.release_id != null) {
        question.release_id = options.release_id;
    }
    if (options?.dev_book != null) {
        question.dev_book = options.dev_book;
    }
    if (options?.gdoc_parent_folder != null) {
        question.gdoc_parent_folder = options.gdoc_parent_folder;
    }
    if (options?.canonical_section_id != null) {
        question.canonical_section_id = options.canonical_section_id;
    }
    if (options?.subject_id != null) {
        question.subject_id = options.subject_id;
    }

    return question;
}

export async function convertProsemirrorTestQuestionToZybook(
    node: JSONContent,
    options?: ConvertProsemirrorToZybookTestQuestionsOptions,
    deps: ConvertProsemirrorTestQuestionsDeps = browserTestQuestionConversionDeps,
): Promise<ZybookTestQuestion> {
    if (node.type !== 'testQuestion') {
        throw new Error('Expected a testQuestion node.');
    }

    const attrs = node.attrs || {};
    const content = node.content || [];
    const instructionsNode = content.find(child => child.type === 'testQuestionInstructions');
    const choiceNodes = content.filter(child => child.type === 'testQuestionChoice');

    const instructionBlocks = await convertProsemirrorBlockNodes(instructionsNode?.content, deps);
    const choices: ZybookTestQuestionNode[] = await Promise.all(
        choiceNodes.map(async choice => ({
            kind: 'block' as const,
            type: 'choice' as const,
            data: choice.attrs?.isCorrect ? { correct: 'true' } : {},
            nodes: flattenInlineParagraphBlocks(await convertProsemirrorBlockNodes(choice.content, deps)),
        })),
    );

    const question: ZybookTestQuestion = {
        test_question_id: typeof attrs.testQuestionId === 'number' ? attrs.testQuestionId : 0,
        payload: {
            kind: 'block',
            type: 'question',
            nodes: [
                {
                    kind: 'block',
                    type: 'instructions',
                    data: {},
                    nodes: instructionBlocks,
                },
                {
                    kind: 'block',
                    type: 'choices',
                    data: {},
                    nodes: choices,
                },
            ],
        },
    };

    return applyQuestionMetadata(question, attrs, options);
}

export async function convertProsemirrorTestQuestionsToZybook(
    doc: JSONContent,
    options?: ConvertProsemirrorToZybookTestQuestionsOptions,
    deps: ConvertProsemirrorTestQuestionsDeps = browserTestQuestionConversionDeps,
): Promise<ZybookTestQuestion[]> {
    if (doc.type !== 'doc') {
        throw new Error('Expected a doc node.');
    }

    const selectedZyVersion = options?.selectedZyVersion ?? null;
    const transformed = deps.transformDoc(doc, selectedZyVersion);
    const docAttrs = transformed.attrs || {};
    const resolvedOptions: ConvertProsemirrorToZybookTestQuestionsOptions = {
        ...options,
        baseImageUrl: options?.baseImageUrl ?? (typeof docAttrs.baseImageUrl === 'string' ? docAttrs.baseImageUrl : null),
        release_id:
            options?.release_id ??
            (typeof docAttrs.releaseId === 'number'
                ? docAttrs.releaseId
                : typeof docAttrs.releaseId === 'string'
                  ? Number(docAttrs.releaseId)
                  : undefined),
    };

    const testQuestionNodes = (transformed.content || []).filter(child => child.type === 'testQuestion');
    return Promise.all(
        testQuestionNodes.map((node, index) =>
            convertProsemirrorTestQuestionToZybook(node, { ...resolvedOptions, number: index + 1 }, deps),
        ),
    );
}
